import { Clickable, Col, Dropdown, Icon, Row, Text } from 'components';
import {
  Api,
  POLL_ROLE,
  POLL_TYPE,
  getPollRole,
  getPollGameLabel,
  theme
} from 'lib';
import useTeamChannelUpdates from 'core/useTeamChannelUpdates';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Header, IrreversableModal, ParticipationSummary } from 'screens/Admin/components';
import Pagination from '../Moderation/components/Pagination';
import PollForm from './components/PollForm';
import PollListTable from './components/PollListTable';
import PollParticipantsModal from './components/PollParticipantsModal';
import PollResultsModal from './components/PollResultsModal';
import PollTeamPicker from './components/PollTeamPicker';

const TYPE_FILTER_OPTIONS = [
  { value: 'All types', opt: undefined },
  { value: 'Game day', opt: POLL_TYPE.GAME_DAY },
  { value: 'Non-game day', opt: POLL_TYPE.NON_GAME_DAY }
];

const ACTIVE_FILTER_OPTIONS = [
  { value: 'All', opt: undefined },
  { value: 'Active', opt: 'true' },
  { value: 'Inactive', opt: 'false' }
];

const PAGE = 20;

// Sentinel for the ?team param meaning "no channel filter" (P4P admins only).
const ALL_CHANNELS = 'all';

const fmtNumber = (n) => (n || 0).toLocaleString();

// The open form's field values ride along in the URL as a JSON `draft` param so a shared
// link reopens the in-progress create/edit exactly as it was left.
const encodeDraft = (obj) => {
  try {
    return JSON.stringify(obj);
  } catch (e) {
    return undefined;
  }
};
const decodeDraft = (str) => {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
};

const Polls = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const role = getPollRole(user);
  const isP4P = role === POLL_ROLE.P4P;

  // Filters live in the URL query string (e.g. ?team=<id>&type=game_day&active=true&game=<id>)
  // so the current view is shareable and bookmarkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const updateParams = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Team admins are scoped to their own channel; P4P admins pick one (rehydrated from
  // the ?team=<id> param on deep-link/refresh).
  const [team, setTeam] = useState(
    role === POLL_ROLE.TEAM
      ? { _id: user._id, name: user.name, username: user.username, avatar: user.avatar }
      : null
  );

  // ?team=all is the cross-channel view. It is deliberately distinct from an absent
  // ?team (nothing picked yet) so the empty state and the "All channels" list don't collide.
  const teamIdParam = searchParams.get('team');
  const allChannels = isP4P && teamIdParam === ALL_CHANNELS;

  useEffect(() => {
    if (!isP4P) return;
    if (!teamIdParam || teamIdParam === ALL_CHANNELS) {
      setTeam(null);
      return;
    }
    if (team?._id === teamIdParam) return;
    let cancelled = false;
    (async () => {
      const res = await Api.post('/admin/getUser', { userId: teamIdParam }, null, {
        silent: true
      });
      if (!cancelled && res?._id) setTeam(res);
    })();
    return () => {
      cancelled = true;
    };
  }, [isP4P, teamIdParam, team?._id]);

  const handleTeamChange = (t) => {
    // A new channel's games no longer apply — drop any match filter alongside it.
    setTeam(t);
    updateParams({ team: t?._id, game: undefined, page: undefined });
  };

  // Matches belong to a single channel, so the cross-channel view drops the match filter.
  const handleSelectAllChannels = () => {
    setTeam(null);
    updateParams({ team: ALL_CHANNELS, game: undefined, page: undefined });
  };

  const typeParam = searchParams.get('type') ?? undefined;
  const activeParam = searchParams.get('active') ?? undefined;
  const gameParam = searchParams.get('game') ?? undefined;
  const typeFilter = TYPE_FILTER_OPTIONS.find((o) => o.opt === typeParam) || TYPE_FILTER_OPTIONS[0];
  const activeFilter =
    ACTIVE_FILTER_OPTIONS.find((o) => o.opt === activeParam) || ACTIVE_FILTER_OPTIONS[0];
  // Switching to a non-game-day type clears the (now irrelevant) match filter.
  // Every filter change resets to page 1 so the list can't land on an out-of-range page.
  const setTypeFilter = (o) =>
    updateParams(
      o.opt === POLL_TYPE.NON_GAME_DAY
        ? { type: o.opt, game: undefined, page: undefined }
        : { type: o.opt, page: undefined }
    );
  const setActiveFilter = (o) => updateParams({ active: o.opt, page: undefined });
  const setGameFilter = (o) => updateParams({ game: o.opt, page: undefined });

  // Pagination lives in the URL (?page=<n>&limit=<n>) so a paged view is shareable.
  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('limit')) || PAGE;

  const [games, setGames] = useState([]);

  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [forbidden, setForbidden] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [resultsPoll, setResultsPoll] = useState(null);
  const [participantsPoll, setParticipantsPoll] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // The create/edit form is also URL-driven (?mode=create | ?mode=edit&id=<pollId>) so an
  // open form survives refresh and is shareable. The edit target resolves from the loaded list.
  const modeParam = searchParams.get('mode');
  const editIdParam = searchParams.get('id');
  const createMode = modeParam === 'create' || modeParam === 'edit' ? modeParam : null;
  const editingPoll = modeParam === 'edit' ? polls.find((p) => p._id === editIdParam) || null : null;
  const initialDraft = useMemo(() => decodeDraft(searchParams.get('draft')), [searchParams]);
  const handleDraftChange = useCallback(
    (draft) => updateParams({ draft: encodeDraft(draft) }),
    [updateParams]
  );

  const teamId = team?._id;
  // Either a channel is picked or "All channels" is active — both are valid scopes to query.
  const scoped = !!teamId || allChannels;

  // Shared by the list and the summary strip so the totals always describe the same rows.
  // teamId is omitted entirely under "All channels"; the backend widens to every channel
  // and gates that on P4P admin.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (teamId) params.set('teamId', teamId);
    if (typeFilter.opt) params.set('pollType', typeFilter.opt);
    if (activeFilter.opt !== undefined) params.set('isActive', activeFilter.opt);
    if (gameParam) params.set('gameId', gameParam);
    return params;
  }, [teamId, typeFilter.opt, activeFilter.opt, gameParam]);

  const buildQuery = useCallback(
    (offset) => {
      const params = buildFilterParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(offset));
      return params.toString();
    },
    [buildFilterParams, pageSize]
  );

  const fetchPolls = useCallback(async () => {
    if (!scoped) return;
    setLoading(true);
    setForbidden(false);
    const res = await Api.get(`/admin/polls/list?${buildQuery((page - 1) * pageSize)}`, null, {
      silent: true
    });
    if (res?.status === 403) {
      setForbidden(true);
      setPolls([]);
      setTotal(0);
    } else if (res?.data) {
      setPolls(res.data);
      setTotal(res.pagination?.total ?? res.data.length);
    }
    setLoading(false);
  }, [scoped, buildQuery, page, pageSize]);

  useEffect(() => {
    if (scoped) fetchPolls();
  }, [scoped, typeFilter.opt, activeFilter.opt, gameParam, page, pageSize, fetchPolls]);

  // Life-to-date totals for exactly the current filter set. Deliberately independent of
  // page/pageSize — the totals describe the whole filtered set, not the visible page.
  const fetchSummary = useCallback(async () => {
    if (!scoped) {
      setSummary(null);
      return;
    }
    setSummaryLoading(true);
    const res = await Api.get(`/admin/polls/summary?${buildFilterParams().toString()}`, null, {
      silent: true
    });
    setSummary(res?.data || null);
    setSummaryLoading(false);
  }, [scoped, buildFilterParams]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Realtime — live-update the table on TEAM_CHANNEL_UPDATE for this channel.
  // Deletes are patched instantly; create/update/state_changed trigger a debounced
  // re-fetch of the current query so filters, totals and game-day state stay correct.
  const refetchTimerRef = useRef(null);
  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => {
      fetchPolls();
      fetchSummary();
    }, 350);
  }, [fetchPolls, fetchSummary]);
  useEffect(() => () => clearTimeout(refetchTimerRef.current), []);

  // Votes arrive far more often than create/update events, so the totals get their own
  // slower debounce — the row itself is already patched in place from the event payload.
  const summaryTimerRef = useRef(null);
  const scheduleSummaryRefetch = useCallback(() => {
    if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
    summaryTimerRef.current = setTimeout(() => fetchSummary(), 2000);
  }, [fetchSummary]);
  useEffect(() => () => clearTimeout(summaryTimerRef.current), []);

  useTeamChannelUpdates(
    teamId,
    useCallback(
      (evt) => {
        if (!evt || evt.entity !== 'poll') return;
        if (evt.action === 'deleted') {
          setPolls((prev) => prev.filter((p) => p._id !== evt.id));
          setTotal((t) => Math.max(0, t - 1));
          scheduleSummaryRefetch();
          return;
        }
        // Every vote → patch this poll's submission count + per-option votes in place
        // from the breakdown (cheap; avoids a full re-fetch on each vote).
        if (evt.action === 'results_updated' && evt.data) {
          const byId = new Map((evt.data.options || []).map((o) => [o.optionId, o.count]));
          setPolls((prev) =>
            prev.map((p) =>
              p._id === evt.id
                ? {
                    ...p,
                    totalSubmissions: evt.data.totalSubmissions ?? p.totalSubmissions,
                    options: (p.options || []).map((o) =>
                      byId.has(o._id) ? { ...o, votes: byId.get(o._id) } : o
                    )
                  }
                : p
            )
          );
          scheduleSummaryRefetch();
          return;
        }
        // created / updated / state_changed → re-fetch the current query.
        scheduleRefetch();
      },
      [scheduleRefetch, scheduleSummaryRefetch]
    )
  );

  // Matches power the "Match" filter (game-day polls only). P4P admins list all of the
  // channel's games via /admin/games/get; team admins use the owner-scoped upcomingGames
  // (games/get is adminOnly → 403 for them). Mirrors PollGamePicker's sourcing.
  useEffect(() => {
    if (!teamId) {
      setGames([]);
      return;
    }
    let cancelled = false;
    (async () => {
      let list = [];
      if (isP4P) {
        const res = await Api.post(
          '/admin/games/get',
          { teamId, sortBy: { date: -1 }, limit: 100 },
          null,
          { silent: true }
        );
        if (res?.games) list = res.games;
      } else {
        const res = await Api.get(`/admin/polls/upcomingGames?teamId=${teamId}&limit=50`, null, {
          silent: true
        });
        if (res?.data) list = res.data;
      }
      if (!cancelled) setGames(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, isP4P]);

  const gameFilterOptions = useMemo(
    () => [
      { value: 'All matches', opt: undefined },
      ...games.map((g) => ({ value: getPollGameLabel(g), opt: g._id }))
    ],
    [games]
  );

  // The dropdown label resolves once the channel's games load; the query itself runs
  // off gameParam directly so a deep-linked ?game=<id> filters immediately.
  const gameFilter = gameFilterOptions.find((o) => o.opt === gameParam) || gameFilterOptions[0];

  const handleCreate = () => {
    if (!teamId) return;
    updateParams({ mode: 'create', id: undefined });
  };

  const handleEdit = (poll) => {
    updateParams({ mode: 'edit', id: poll._id });
  };

  const closeForm = () => updateParams({ mode: undefined, id: undefined, draft: undefined });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await Api.delete(`/admin/polls/delete/${deleteTarget._id}`, null, null, {
      silent: true
    });
    setDeleting(false);
    if (ok === true) {
      setPolls((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      setTotal((t) => Math.max(0, t - 1));
      fetchSummary();
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Poll deleted.' } });
      setDeleteTarget(null);
    } else {
      // 404 means it's already gone — drop it from the list and inform the admin.
      if (ok?.status === 404) {
        setPolls((prev) => prev.filter((p) => p._id !== deleteTarget._id));
        dispatch({ type: 'HANDLE_RES', payload: { err: 'Poll was already deleted.' } });
      } else {
        dispatch({
          type: 'HANDLE_RES',
          payload: { err: ok?.err || 'Could not delete the poll. Please try again.' }
        });
      }
      setDeleteTarget(null);
    }
  };

  const handleSaved = () => {
    fetchPolls();
    fetchSummary();
    dispatch({ type: 'HANDLE_RES', payload: { res: 'Poll saved.' } });
  };

  const handleToggleActive = (updated) => {
    setPolls((prev) => prev.map((p) => (p._id === updated._id ? { ...p, ...updated } : p)));
  };

  if (!role) {
    return (
      <Col centerAll ht="60vh">
        <Text h3>Not authorized</Text>
        <Text col={theme.GREY_60} marg="8px 0 0">
          Poll management is available to P4P admins and channel owners only.
        </Text>
      </Col>
    );
  }

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center>
          <Text h3>Polls</Text>
          <Clickable
            onClick={handleCreate}
            style={{
              opacity: teamId ? 1 : 0.4,
              cursor: teamId ? 'pointer' : 'not-allowed'
            }}
            title={
              teamId
                ? 'Create a poll'
                : allChannels
                  ? 'Pick a single channel first'
                  : 'Pick a channel first'
            }
          >
            <Icon marg="0 0 0 8px" type="add" dimensions={18} />
          </Clickable>
        </Row>
      </Header>

      <Col start noFlex marg="54px 0 0" wid="100%" pad="0 24px 24px">
        <Row
          start
          gap="30px"
          noFlex
          wrap
          style={{
            alignItems: 'flex-end',
            position: 'sticky',
            top: 54,
            background: 'white',
            zIndex: 2,
            padding: '12px 24px',
            marginLeft: -24,
            marginRight: -24,
            borderBottom: `1px solid ${theme.GREY_20}`
          }}
        >
          {isP4P && (
            <Col noFlex>
              <Text marg="0 0 4px" b2 med>
                Channel
              </Text>
              <PollTeamPicker
                team={team}
                onChange={handleTeamChange}
                label={null}
                allChannels={allChannels}
                onSelectAll={handleSelectAllChannels}
              />
            </Col>
          )}

          <Dropdown
            admin
            title="Type"
            wid="180px"
            selected={typeFilter.value}
            options={TYPE_FILTER_OPTIONS}
            handleSelect={setTypeFilter}
            rounded
            small
          />

          <Dropdown
            admin
            title="Status"
            wid="160px"
            selected={activeFilter.value}
            options={ACTIVE_FILTER_OPTIONS}
            handleSelect={setActiveFilter}
            rounded
            small
          />

          {/* Matches are per-channel, so there is nothing to filter by across all channels. */}
          {!allChannels &&
            gameFilterOptions.length > 1 &&
            typeFilter.opt !== POLL_TYPE.NON_GAME_DAY && (
            <Dropdown
              admin
              title="Match"
              wid="260px"
              selected={gameFilter.value}
              options={gameFilterOptions}
              handleSelect={setGameFilter}
              rounded
              small
            />
          )}

          {scoped && !forbidden && (
            <Col noFlex pad="0 0 12px">
              <Text c2 col={theme.GREY_60}>
                {total} poll{total === 1 ? '' : 's'}
              </Text>
            </Col>
          )}
        </Row>

        {!scoped ? (
          <Col centerAll wid="100%" pad="80px 0">
            <Text col={theme.GREY_60} h4>
              {isP4P ? 'Pick a channel to see its polls.' : 'Loading your channel...'}
            </Text>
            {isP4P && (
              <Text col={theme.GREY_60} marg="8px 0 0" center>
                Then use the + next to the title to create a game-day or non-game-day poll.
              </Text>
            )}
          </Col>
        ) : forbidden ? (
          <Col centerAll wid="100%" pad="80px 0">
            <Text h4>No access to this channel</Text>
            <Text col={theme.GREY_60} marg="8px 0 0" center>
              You can only manage polls for your own channel.
            </Text>
          </Col>
        ) : (
          <>
            <ParticipationSummary
              loading={summaryLoading}
              note={
                allChannels
                  ? 'Life-to-date totals across every channel, for the filters above.'
                  : 'Life-to-date totals for this channel, for the filters above.'
              }
              items={[
                { label: 'Polls', value: fmtNumber(summary?.polls) },
                { label: 'Votes', value: fmtNumber(summary?.votes) },
                { label: 'Unique voters', value: fmtNumber(summary?.uniqueVoters) },
                { label: 'Points awarded', value: fmtNumber(summary?.pointsAwarded) }
              ]}
            />
            <PollListTable
              polls={polls}
              loading={loading}
              isP4P={isP4P}
              showChannel={allChannels}
              onEdit={handleEdit}
              onDelete={setDeleteTarget}
              onViewResults={setResultsPoll}
              onViewParticipants={setParticipantsPoll}
              onToggleActive={handleToggleActive}
            />
            {!loading && total > 0 && (
              <Pagination
                page={page}
                totalPages={Math.max(1, Math.ceil(total / pageSize))}
                pageSize={pageSize}
                total={total}
                onPageSizeChange={(size) => updateParams({ limit: size, page: undefined })}
                onPrev={() => updateParams({ page: page - 1 })}
                onNext={() => updateParams({ page: page + 1 })}
                onGoToPage={(p) => updateParams({ page: p })}
              />
            )}
          </>
        )}
      </Col>

      {/* Panels stay mounted so the Sidepanel transition runs on open and close. */}
      <PollForm
        // For edit, wait until the target poll resolves from the list before opening so the
        // form seeds its inputs with the existing values (matters on deep-link / refresh).
        visible={createMode === 'create' || (createMode === 'edit' && !!editingPoll)}
        teamId={teamId}
        mode={createMode || 'create'}
        poll={editingPoll}
        initialDraft={initialDraft}
        onDraftChange={handleDraftChange}
        onClose={closeForm}
        onSaved={handleSaved}
      />

      <PollResultsModal
        visible={!!resultsPoll}
        poll={resultsPoll}
        onClose={() => setResultsPoll(null)}
      />

      <PollParticipantsModal
        visible={!!participantsPoll}
        poll={participantsPoll}
        onClose={() => setParticipantsPoll(null)}
      />

      <IrreversableModal
        visible={!!deleteTarget}
        heading="Delete poll?"
        topText={deleteTarget ? `"${deleteTarget.question}"` : ''}
        text="This action cannot be undone. Existing votes will be removed."
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        waiting={deleting}
        onConfirm={confirmDelete}
        closeModal={() => !deleting && setDeleteTarget(null)}
        confirmNoClose
      />
    </Col>
  );
};

export default Polls;
